import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import { WithLoader } from 'Components/WithLoader'
import { FaultReportUpdateList } from 'Services/SalesManagement/Features/FaultReport/Components/FaultReportUpdateList'
import {
  FaultReportType,
  fetchFaultReport,
  fetchFaultReportOptions,
  fetchFaultReportUpdates
} from 'Services/SalesManagement/Features/FaultReport/faultReportSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { datetimeFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { Grid } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export default function FaultReportDetailViewPage() {
  const dispatch = useAppDispatch()
  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const {
    singleFaultReport,
    optionsLoading,
    loading,
    faultReportUpdateLoading,
    faultReportUpdates
  } = useTypedSelector(state => state.faultReport)

  useEffect(() => {
    dispatch(fetchFaultReportOptions())
    dispatch(
      fetchFaultReport({
        id: instanceIdParam
      })
    )
      .then(unwrapResult)
      .catch(() => {
        SnackbarUtils.error(t`There was an error fetching the fault report.`)
      })
    dispatch(
      fetchFaultReportUpdates({
        faultReportId: instanceIdParam
      })
    )
      .then(unwrapResult)
      .catch(() => {
        SnackbarUtils.error(t`There was an error fetching the fault report updates.`)
      })
  }, [dispatch])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const getFaultReportType = (type: string) => {
    return type === FaultReportType.unplanned ? t`Current incident` : t`Planned service break`
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: t`View fault report`
    }
  ]

  const isLoading = loading === 'pending' || optionsLoading === 'pending'

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={SALES_MANAGEMENT_ROOT_URL} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h2">
              {singleFaultReport && getFaultReportType(singleFaultReport.type)}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h1">{singleFaultReport?.title_fi}</Typography>
            </Box>
            <Box sx={{ my: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t`Start time`}:{' '}
                {singleFaultReport && datetimeFormatter(singleFaultReport.start_time)}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t`Updated`}: {singleFaultReport && datetimeFormatter(singleFaultReport.updated)}
              </Typography>
            </Box>
            <Box sx={{ my: 6 }}>
              {singleFaultReport && (
                <Typography variant="body1" color="text.primary">
                  <div
                    style={{ whiteSpace: 'pre-wrap' }}
                    // Only the superusers can edit this content, so it should be safe to use dangerouslySetInnerHTML here
                    dangerouslySetInnerHTML={{
                      __html: singleFaultReport.description_fi
                        ? singleFaultReport.description_fi
                        : ''
                    }}
                  ></div>
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FaultReportUpdateList
              faultReportId={instanceIdParam}
              faultReportUpdates={faultReportUpdates}
              hasControlRights={false}
              isNewInstance={false}
              isLoading={faultReportUpdateLoading === 'pending'}
            />
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
